/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { COMMODITY_COLOURS } from 'baselode';

const ASSAY_COLOR_PALETTE_10 = [
  '#1d1147',
  '#3b0f70',
  '#641a80',
  '#8c2981',
  '#b73779',
  '#de4968',
  '#f7705c',
  '#fe9f6d',
  '#fece91',
  '#fcfdbf',
];

export { ASSAY_COLOR_PALETTE_10 };

export function buildAssayIntervalsByHole(assayHoles) {
  const byHole = {};
  (assayHoles || []).forEach((hole) => {
    const holeId = hole?.id;
    if (!holeId) return;
    const seen = new Set();
    const intervals = [];
    (hole.points || []).forEach((point) => {
      const from = Number(point?.from ?? point?.samp_from ?? point?.fromdepth ?? point?.from_depth ?? point?.depth_from);
      const to = Number(point?.to ?? point?.samp_to ?? point?.todepth ?? point?.to_depth ?? point?.depth_to);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
      const key = `${from}:${to}`;
      if (seen.has(key)) return;
      seen.add(key);
      intervals.push({ from, to, values: { ...point } });
    });
    if (intervals.length) {
      const sorted = intervals.sort((a, b) => a.from - b.from);
      const aliases = new Set([
        normalizeHoleKey(holeId),
        normalizeHoleKey(sorted[0]?.values?.hole_id),
        normalizeHoleKey(sorted[0]?.values?.holeid),
        normalizeHoleKey(sorted[0]?.values?.anumber),
        normalizeHoleKey(sorted[0]?.values?.id),
      ]);
      aliases.forEach((alias) => { if (alias) byHole[alias] = sorted; });
    }
  });
  return byHole;
}

export function mapIntervalsForVariable(intervalsByHole, variable) {
  if (!variable) return null;
  const mapped = {};
  Object.entries(intervalsByHole || {}).forEach(([holeId, intervals]) => {
    const normalizedHoleId = normalizeHoleKey(holeId);
    const entries = (intervals || []).map((interval) => ({
      from: interval.from, to: interval.to,
      value: Number(interval?.values?.[variable]),
    })).filter((entry) => Number.isFinite(entry.value));
    if (entries.length) mapped[normalizedHoleId] = entries;
  });
  return mapped;
}

export function buildEqualRangeColorScale(values = [], colors = ASSAY_COLOR_PALETTE_10) {
  let min = Infinity, max = -Infinity, count = 0;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    count += 1;
  }
  if (!count) return { min: null, max: null, step: null, bins: [], colors };
  const binCount = colors.length;
  if (max === min) {
    return { min, max, step: 0, bins: colors.map((_, i) => ({ index: i, min, max, label: `${min}` })), colors };
  }
  const step = (max - min) / binCount;
  const bins = colors.map((_, index) => {
    const lower = min + step * index;
    const upper = index === binCount - 1 ? max : min + step * (index + 1);
    return { index: index, min: lower, max: upper, label: `${lower.toFixed(3)} - ${upper.toFixed(3)}` };
  });
  return { min, max, step, bins, colors };
}

export function normalizeHoleKey(value) {
  return `${value ?? ''}`.trim().toLowerCase();
}

export function finiteValueRange(values) {
  let min = Infinity;
  let max = -Infinity;
  let count = 0;
  for (const value of values || []) {
    if (!Number.isFinite(value)) continue;
    if (value < min) min = value;
    if (value > max) max = value;
    count += 1;
  }
  return count ? { min, max } : null;
}

export function buildCategoryIntervalsByHole(holes, variable) {
  const byHole = {};
  (holes || []).forEach((hole) => {
    const holeId = hole?.id || hole?.holeId;
    if (!holeId) return;
    const seen = new Set();
    const intervals = [];
    (hole.points || []).forEach((point) => {
      const from = Number(point?.from);
      const to = Number(point?.to);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
      const key = `${from}:${to}`;
      if (seen.has(key)) return;
      seen.add(key);
      const value = point[variable];
      if (value == null || String(value).trim() === '') return;
      intervals.push({ from, to, value: String(value).trim() });
    });
    if (intervals.length) byHole[normalizeHoleKey(holeId)] = intervals.sort((a, b) => a.from - b.from);
  });
  return byHole;
}

export function commodityColorForVariable(variable) {
  if (!variable) return '#8b1e3f';
  const tokens = variable.split(/[_\-/\s]+/);
  for (const token of tokens) {
    if (Object.prototype.hasOwnProperty.call(COMMODITY_COLOURS, token)) return COMMODITY_COLOURS[token];
    const low = token.toLowerCase();
    for (const [key, colour] of Object.entries(COMMODITY_COLOURS)) {
      if (key.toLowerCase() === low) return colour;
    }
  }
  return '#8b1e3f';
}
